<template>
    <div class="step-2-information border-bottom-line">
        <h3 class="personal-text font-heading">{{ label }}</h3>
        <div class="form-row existing-horse">
            <div class="col-md-6">
                <fieldset class="form-group name-input">
                    <input
                        class="form-control"
                        type="text"
                        :required="isRequired"
                        :ref="getRefKey"
                        :id="getRefKey + '_name'"
                        :disabled="!canEditName"
                        v-model="value.name"
                        @keyup="onNameChanged"
                    />
                    <label
                        class="form-control-placeholder"
                        :class="{ loaded: !!value.name || !canEditLifeID }"
                        :for="getRefKey + '_name'"
                    >
                        {{ nameLabel | trans }}
                    </label>
                </fieldset>
            </div>
            <div class="col-md-6">
                <fieldset class="lifeid " :class="{ disable: !canEditLifeID }">
                    <select
                        class="form-control w-200-select"
                        :class="`${getRefKey}_lifeIDPrefixSelect`"
                        :required="isRequired"
                        data-role="select"
                        data-filter="false"
                        :ref="`${getRefKey}_lifeIDPrefixSelect`"
                        v-model="lifeIDPrefix"
                        @itemselect="onLifeIDTyped"
                    >
                        <option
                            :value="lifePrefix"
                            v-for="lifePrefix in lifeIDPrefixList"
                            :key="lifePrefix"
                            >{{ lifePrefix }}</option
                        >
                        <option value="NV" class="d-none">NV</option>
                    </select>

                    <label class="form-control-placeholder" :for="`${getRefKey}_life_id`">
                        {{ 'javascript.horse_registration.life_id' | trans }}
                    </label>
                    <input
                        class="form-control"
                        type="text"
                        :id="`${getRefKey}_life_id`"
                        v-model="lifeIDValue"
                        @keyup="onLifeIDTyped"
                        :required="isRequired"
                    />
                </fieldset>
            </div>
        </div>
        <input type="text" :name="name + '[name]'" hidden :value="value.name" />
        <input type="text" :name="name + '[life_id]'" hidden v-model="lifeID" />
        <input type="text" :name="name + '[old_life_id]'" hidden v-model="oldLifeID" />
        <input type="text" :name="name + '[is_registered]'" hidden :value="!value.is_loaded" />
        <input type="text" :name="name + '[updated]'" hidden :value="1" v-if="isDataUpdated" />
    </div>
</template>
<script>
import { EventBus } from './event-bus'

export default {
    props: {
        parentPrefix: {
            type: String,
            default: 'V',
        },
        name: {
            type: String,
            default: null,
        },
        nameLabel: {
            type: String,
            default: 'javascript.horse_registration.name',
        },
        value: {
            type: Object,
            default() {
                return {
                    name: null,
                    life_id: null,
                }
            },
        },
        horseId: {
            type: Number,
            required: false,
        },
        label: {
            type: String,
            default: 'Parent Information',
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        levelParent: {
            type: String,
            default: 2,
        },
        isStallion: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            parentName: '',
            oldLifeID: '',
            originalName: '',
            nameChanged: false,
            lifeIDTyped: false,
            lifeIDPrefix: 'DE',
            lifeIDSurfix: ' ',
            lifeIDPrefixList: [
                'DE',
                'NLD',
                'BEL',
                'DNK',
                'SWE',
                'FIN',
                'AUT',
                'CHE',
                'FRA',
                'GBR',
                'IRL',
                'ESP',
                'ITA',
                'POL',
                'HUN',
            ],
            isLoaded: false,
        }
    },
    inject: ['isParentDataLoaded', 'getParentRef', 'horseId'],
    computed: {
        canEditName() {
            return !this.isParentLoaded
        },
        hiddenFields() {
            return {
                name: this.prentName,
                life_id: this.lifeID,
            }
        },
        lifeIDChanged() {
            return this.lifeID !== this.oldLifeID
        },
        canEditLifeID() {
            return !this.value.is_loaded && !this.isParentLoaded
        },
        getParentPrefix() {
            return this.parentPrefix.substring(0, this.parentPrefix.length - 1)
        },
        isParentLoaded() {
            return this.getParentPrefix && this.isParentDataLoaded(this.getParentPrefix)
        },
        hasGeneratedLifeID() {
            return this.lifeID && this.lifeID.startsWith('NV 1')
        },
        isDataUpdated() {
            return (
                !this.isParentLoaded &&
                !this.value.is_loaded &&
                ((this.lifeIDChanged && this.lifeIDTyped) || this.nameChanged)
            )
        },
        lifeID: {
            get() {
                return this.canEditLifeID ? this.lifeIDPrefix + ' ' + this.lifeIDSurfix : this.value.life_id
            },
            set(newVal) {
                this.setupLifeID(newVal)
            },
        },
        getRefKey() {
            return this.getParentRef(this.parentPrefix)
        },
        lifeIDValue: {
            get() {
                return this.canEditLifeID ? this.lifeIDSurfix : this.value.life_id
            },
            set(newValue) {
                this.lifeIDSurfix = newValue
            },
        },
    },
    methods: {
        reducedFilter(data, keys, fn) {
            data.filter(fn).map(el =>
                keys.reduce((acc, key) => {
                    acc[key] = el[key]
                    return acc
                }, {}),
            )
        },

        updateData() {
            // Emit data to parent component on each update
            this.$emit('input', {
                name: this.$refs[this.getRefKey].value,
                life_id: this.lifeID,
                is_loaded: this.value.is_loaded,
            })
        },

        initTypeHead() {
            const emptyMessage = [
                '<div class="empty-message">',
                this.$options.filters.trans(
                    'javascript.horse_registration.pedigree_not_found_message',
                ),
                '</div>',
            ].join('\n')
            this.createTypehead('name', 'name', 1, 10, emptyMessage)
        },

        initLifeIDTypeHead() {
            this.createTypehead('life_id', 'life_id', 10, 1, null)
        },

        createTypehead(elementType, field, minLength, limit, empty) {
            const url = route('pedigree.search')
            let self = this

            let fullUrl = `${url}?query=%QUERY&parent_type=${this.isStallion ? 'V' : 'M'}`
            if (elementType == 'life_id') {
                fullUrl += `&query_type=life_id`
            }
            const horseType = self.getHorseSubType()
            if (horseType) {
                fullUrl += `&horse_type=${horseType}`
            }

            fullUrl += `&limit=${limit}`


            $(`#${this.getRefKey}_${field}`).typeahead('destroy')

            let suggestion = new Bloodhound({
                datumTokenizer: function(datum) {
                    return Bloodhound.tokenizers.whitespace(datum.value)
                },
                queryTokenizer: Bloodhound.tokenizers.whitespace,
                remote: {
                    url: fullUrl,
                    wildcard: '%QUERY',
                },
            })

            // Init typeahead on popover name input
            // When selecting a suggestion  we get the formatted pedigree from server
            $(`#${this.getRefKey}_${field}`)
                .typeahead(
                    {
                        hint: true,
                        highlight: true,
                        minLength: minLength,
                        limit: limit,
                        classNames: {
                            menu: 'typeahead-menu list',
                        },
                    },
                    {
                        source: function(query, sync, async) {
                            if (self.canEditName) {
                                suggestion.ttAdapter()(query, sync, async)
                            }
                        },
                        templates: {
                            suggestion: function(data) {
                                if (data.life_id) {
                                    return `<div class="list-item"><strong>${data.name}</strong> &nbsp;&nbsp; <span class='lifeid-sufix'>${data.life_id}</span></div>`
                                } else {
                                    return `<div class="list-item"><strong>${data.name}</strong></div>`
                                }
                            },
                            empty,
                        },
                        display: 'name',
                    },
                )
                .bind('typeahead:select', function(ev, suggestion) {
                    self.loading = true
                    self.value[elementType] = suggestion.name
                    axios
                        .get(
                            route('pedigree.by-level', {
                                level: self.levelParent,
                                prefix: self.parentPrefix,
                            }),
                            {
                                params: {
                                    child: suggestion.id,
                                },
                            },
                        )
                        .then(response => {
                            self.loading = false
                            self.$emit('newData', response.data)
                            self.$nextTick(() => {
                                $(`#${self.getRefKey}_${field}`).val(suggestion.name)
                            })
                        })
                })
                .bind('typeahead:close', function(ev, suggestion) {
                    $(`#${self.getRefKey}_${field}`)
                        .typeahead('val', self.value[elementType])
                        .typeahead('close')
                })
                .bind('typeahead:asyncrequest', function(eob, currentQuery, dataset) {
                    self.loading = true
                })
                .bind('typeahead:asynccancel', function(eob, currentQuery, dataset) {
                    self.loading = false
                })
                .bind('typeahead:asyncreceive', function(eob, currentQuery, dataset) {
                    self.loading = false
                })
        },

        onNameChanged() {
            this.nameChanged = this.value.name !== this.originalName
            if (this.value.is_loaded) {
                EventBus.$emit(`is-typing-${this.horseId || 0}`, this.parentPrefix)
                this.resetData(true)
            }
        },
        onLifeIDTyped() {
            this.$nextTick(() => {
                this.lifeIDTyped = true
                if (this.value.is_loaded) {
                    EventBus.$emit(`is-typing-${this.horseId || 0}`, this.parentPrefix)
                    this.resetData(true)
                }
            })
        },
        resetData(excludeName = false) {
            this.lifeIDSurfix = ''
            this.oldLifeID = null
            this.$emit('input', {
                name: excludeName ? this.value.name : null,
                life_id: this.lifeIDPrefix + ' ',
                is_loaded: false,
            })
        },
        updateMetroSelect() {
            const selectElement = this.$refs[`${this.getRefKey}_lifeIDPrefixSelect`]

            if (selectElement) {
                const metroSelect = $(selectElement).data('select')
                if (metroSelect) {
                    metroSelect.val(this.lifeIDPrefix)
                }
            }
        },
        setupLifeID(value) {
            const result = this.getLifeIDPrefixeAndSurfix(value)
            this.lifeIDPrefix = result.prefix
            this.lifeIDSurfix = result.surfix
        },
        getLifeIDPrefixeAndSurfix(value) {
          const regex = /^([A-Z]+)\s*([A-Z0-9]+)$/;
          let lifeIDPrefix = 'DE'
          let lifeIDSurfix = ''
            if (value) {
                const match = value.match(regex)
                if (match) {
                    lifeIDPrefix = match[1]
                    lifeIDSurfix = match[2]
                }
            }

            return {
                prefix: lifeIDPrefix,
                surfix: lifeIDSurfix,
            }
        },
        getHorseSubType() {
            if ($(`#checkbox-warmblood${this.horseId || ''}`).is(':checked')) {
                return 'Warmblood'
            } else if ($(`#checkbox-pony${this.horseId || ''}`).is(':checked')) {
                return 'Pony'
            } else {
                return ''
            }
        },
    },
    mounted() {
        const lifeID = this.value.life_id
        this.setupLifeID(lifeID)

        this.initTypeHead()

        // if (!this.value.is_loaded) {
        //     this.initLifeIDTypeHead()
        // }

        EventBus.$on(`is-initialized-${this.horseId || 0}`, () => {
            this.oldLifeID = this.value.life_id
            this.originalName = this.value.name
            this.originalData = this.value
        })
        EventBus.$on(`is-loaded-${this.horseId || 0}`, () => {
            if (this.lifeIDChanged && this.lifeIDTyped) {
                this.lifeIDTyped = false
            }
        })
        EventBus.$on(`is-typing-${this.horseId || 0}`, event => {
            if (
                event !== this.parentPrefix &&
                this.parentPrefix.startsWith(event) &&
                (this.isParentLoaded || this.value.is_loaded)
            ) {
                this.resetData()
            }
        })
        $(`#checkbox-warmblood${this.horseId || ''}, #checkbox-pony${this.horseId || ''}`).on(
            'change',
            () => {
                this.initTypeHead()
            },
        )
    },
    watch: {
        'value.life_id'() {
            const lifeID = this.value.life_id
            const result = this.getLifeIDPrefixeAndSurfix(lifeID)
            this.lifeIDSurfix = result.surfix
            if (result.prefix !== this.lifeIDPrefix) {
                this.lifeIDPrefix = result.prefix
                this.$nextTick(() => {
                    this.updateMetroSelect()
                })
            }
        },
        // 'value.is_loaded'() {
        //     if (!this.value.is_loaded) {
        //         this.initLifeIDTypeHead()
        //     }
        // },
    },
    destroyed() {
        // Destroy event listening
        EventBus.$off(`is-initialized-${this.horseId || 0}`)
        EventBus.$off(`is-loaded-${this.horseId || 0}`)
    },
}
</script>
